import { inject, Injectable } from "@angular/core";
import { catchError, debounceTime, EMPTY, from, Observable, share, switchMap } from "rxjs";
import { rowIdFor } from "../../db/rowid";
import { opsMissionTypeEnum, property } from "../../plugins/opsctrl-foc/dataStruct";
import { SyncChangesService } from "../../sync/sync-changes.service";
import { DatabaseService } from "../database/database.service";
import {
  AIRCRAFT_SUFFIX,
  AircraftFuel,
  AircraftTimer,
  fuelRowValues,
  parseFuelRow,
  parseTimerRow,
  sameTimer,
  shouldAdopt,
  timerRowValues,
} from "./aircraft-state";

@Injectable({ providedIn: "root" })
export class AircraftStateService {
  private readonly db = inject(DatabaseService);
  private readonly sync = inject(SyncChangesService);
  private registrationId = "";
  private known: AircraftFuel | null = null;
  private knownTimerValue: AircraftTimer | null = null;

  get knownTimer(): AircraftTimer | null {
    return this.knownTimerValue;
  }

  get knownFuel(): AircraftFuel | null {
    return this.known;
  }

  // Emits the stored figure when it differs from the one this device knows.
  readonly fuelAdopted$: Observable<AircraftFuel> = this.sync.changesNotOwnChain("param").pipe(
    debounceTime(1000),
    switchMap(() =>
      from(this.readFuel()).pipe(
        switchMap((stored) => {
          if (stored && shouldAdopt(this.known, stored)) {
            this.known = stored;
            return [stored];
          }
          return EMPTY;
        }),
        catchError((e) => {
          console.warn("aircraft fuel read failed", e);
          return EMPTY;
        })
      )
    ),
    share()
  );

  readonly timerChanged$: Observable<AircraftTimer> = this.sync.changesNotOwnChain("param").pipe(
    debounceTime(1000),
    switchMap(() =>
      from(this.readTimer()).pipe(
        switchMap((stored) => {
          if (stored && !sameTimer(this.knownTimerValue, stored)) {
            this.knownTimerValue = stored;
            return [stored];
          }
          return EMPTY;
        }),
        catchError((e) => {
          console.warn("aircraft timer read failed", e);
          return EMPTY;
        })
      )
    ),
    share()
  );

  private async registration(): Promise<string> {
    if (this.registrationId) return this.registrationId;
    const rows = await this.db.namedQuery("myRegistration", {});
    const id = rows?.[0]?.id;
    if (typeof id === "string" && id) this.registrationId = id;
    return this.registrationId;
  }

  private async readRow(paramTypeId: number): Promise<{ value: string; json: string } | null> {
    const parameterHash = await this.registration();
    if (!parameterHash) return null;
    const rows = await this.db.namedQuery("paramByHashSuffixTypeMission", {
      parameterHash,
      opsMissionType: opsMissionTypeEnum.all,
      tableSuffix: AIRCRAFT_SUFFIX,
      paramTypeId,
    });
    const row = rows?.[0];
    return row ? { value: row.paramValString, json: row.paramValJson } : null;
  }

  private async writeRow(typeId: number, values: { value: string; json: string }, at: number): Promise<void> {
    const parameterHash = await this.registration();
    if (!parameterHash) throw new Error("registration unknown");
    const paramTypeId = String(typeId);
    await this.db.namedCommand("paramInsert", {
      rowId: rowIdFor([parameterHash, paramTypeId, AIRCRAFT_SUFFIX, opsMissionTypeEnum.all]),
      timestamp: String(at),
      parameterHash,
      paramTypeId,
      opsMissionType: opsMissionTypeEnum.all,
      tableSuffix: AIRCRAFT_SUFFIX,
      paramValString: values.value,
      paramValBlob: "",
      paramValJson: values.json,
      paramValGeoJson: "",
      paramValMarkDown: "",
      updatedAt: at,
    });
  }

  async readFuel(): Promise<AircraftFuel | null> {
    const row = await this.readRow(property.acftFuelOnBoard);
    return row ? parseFuelRow(row) : null;
  }

  async writeFuel(kg: number, at: number = Date.now(), arrear: number = 0): Promise<AircraftFuel> {
    await this.writeRow(property.acftFuelOnBoard, fuelRowValues({ kg, at, arrear }), at);
    this.known = arrear > 0 ? { kg, at, arrear } : { kg, at };
    return this.known;
  }

  async loadFuel(): Promise<AircraftFuel | null> {
    const stored = await this.readFuel();
    if (stored) this.known = stored;
    return stored;
  }

  async readTimer(): Promise<AircraftTimer | null> {
    const row = await this.readRow(property.acftFlightTimer);
    return row ? parseTimerRow(row) : null;
  }

  async writeTimer(t: AircraftTimer, at: number = Date.now()): Promise<AircraftTimer> {
    await this.writeRow(property.acftFlightTimer, timerRowValues(t), at);
    this.knownTimerValue = t;
    return t;
  }

  async loadTimer(): Promise<AircraftTimer | null> {
    const stored = await this.readTimer();
    if (stored) this.knownTimerValue = stored;
    return stored;
  }
}
